import { Box, Stack } from '@mui/system'
import { FC } from 'react'
import { ProcessedActivity, TwoWayAudioInteractionOutdoorActivity } from '../../../../../../api/models'
import { CompleteTwoWayAudioInteractionOutdoorActivityResultAudioQuality } from '../../../../../../api/generated/museum-v1-types'
import GoodQualityIcon from '../../../../../../assets/images/good-quality.svg?react'
import FairQualityIcon from '../../../../../../assets/images/fair-quality.svg?react'
import PoorQualityIcon from '../../../../../../assets/images/poor-quality.svg?react'
import UnknownQualityIcon from '../../../../../../assets/images/unknown-quality.svg?react'
import { ActivityFieldBox } from '../ActivityFieldBox'
import { MISSING_VALUE } from '../../../../../../constants'
import { SharedActivity, SharedActivityProps } from './SharedActivity'
import { EqualColumnsGrid } from '../../EqualColumnsGrid'
import { camelCaseToSplitWords } from '../../../activityUtils'

type TwoWayAudioInteractionActivityProps = SharedActivityProps & {
  activity: Extract<ProcessedActivity, TwoWayAudioInteractionOutdoorActivity>
}

function audioQualityIcon(quality: string | undefined) {
  switch (quality) {
    case CompleteTwoWayAudioInteractionOutdoorActivityResultAudioQuality.Good:
      return <GoodQualityIcon />
    case CompleteTwoWayAudioInteractionOutdoorActivityResultAudioQuality.Fair:
      return <FairQualityIcon />
    case CompleteTwoWayAudioInteractionOutdoorActivityResultAudioQuality.Poor:
      return <PoorQualityIcon />
    default:
      return <UnknownQualityIcon />
  }
}

export const TwoWayAudioInteractionActivity: FC<TwoWayAudioInteractionActivityProps> = ({ activity, engagement, timing }) => {
  const activityResult = engagement.result?.twoWayAudioInteraction
  const failedReason = activityResult && 'reason' in activityResult && activityResult.reason
  const participantNames = activityResult?.participants?.map(p => ('name' in p ? p.name : p.id)).join(', ') ?? MISSING_VALUE

  return (
    <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
      <SharedActivity activity={activity} engagement={engagement} timing={timing} />
      {(failedReason || activityResult?.audioQuality) && (
        <EqualColumnsGrid>
          {failedReason && (
            <ActivityFieldBox label="Reason if failed" labelTestId="two-way-audio-interaction-failed-reason">
              <Box>{camelCaseToSplitWords(failedReason)}</Box>
            </ActivityFieldBox>
          )}
          {activityResult?.audioQuality && (
            <ActivityFieldBox label="Audio Quality" labelTestId="activity-card-audio-quality">
              <Stack direction="row" sx={{ alignItems: 'center', gap: 'var(--spacing-3xs)' }}>
                {audioQualityIcon(activityResult?.audioQuality)}
                <Box data-testid="activity-card-audio-quality-value">{activityResult?.audioQuality}</Box>
              </Stack>
            </ActivityFieldBox>
          )}
        </EqualColumnsGrid>
      )}

      {(activityResult?.participants?.length || activityResult?.statedPurpose) && (
        <Box>
          {activityResult?.participants?.length && (
            <ActivityFieldBox combinedWith="below" label="Participants" labelTestId="two-way-audio-interaction-participants">
              <Box>{participantNames}</Box>
            </ActivityFieldBox>
          )}
          {activityResult?.statedPurpose && (
            <ActivityFieldBox combinedWith="above" label="2WA Stated Purpose" labelTestId="two-way-audio-interaction-stated-purpose">
              <Box data-testid="activity-card-stated-purpose-value">{activityResult?.statedPurpose}</Box>
            </ActivityFieldBox>
          )}
        </Box>
      )}
    </Stack>
  )
}
